import React from 'react';
import { activityEmoji, tilePalette } from '../../games_components/activityTheme';

export const STATUS_META = {
  live: { label: 'Live', hint: 'Shown to users on Activities and in the game picker.' },
  pending: { label: 'Pending review', hint: 'Submitted but not reviewed. Not shown to users.' },
  hidden: { label: 'Hidden', hint: 'Not shown to users.' },
};

const SUMMARY_KEYS = [
  'id', 'title', 'body', 'status', 'app_only', 'focus', 'game_type', 'tag_ids', 'picture_count',
  'has_practice', 'cover_url', 'author', 'created_at', 'updated_at',
];

export function summaryFromDetail(detail) {
  const summary = {};
  SUMMARY_KEYS.forEach((key) => { summary[key] = detail[key]; });
  return summary;
}

export function StatusPill({ status }) {
  return <span className={`aaStatus aaStatus--${status}`}>{STATUS_META[status].label}</span>;
}

export function CoverTile({ activity, size = 48 }) {
  const style = { width: size, height: size, borderRadius: Math.round(size * 0.28) };
  if (activity.cover_url) {
    return <img className="aaCover" style={style} src={activity.cover_url} alt="" loading="lazy" />;
  }
  const palette = tilePalette(activity.id || 0);
  return (
    <span
      className="aaCover aaCover--emoji"
      aria-hidden="true"
      style={{
        ...style,
        fontSize: Math.round(size * 0.5),
        background: `linear-gradient(135deg, ${palette.tile[0]}, ${palette.tile[1]})`,
      }}
    >
      {activityEmoji(activity)}
    </span>
  );
}

export function gameTypeLabel(gameTypes, key) {
  const type = gameTypes.find((t) => t.key === key);
  return type ? type.label : key;
}

// Mirrors the game picker rule in app/LexodyAPI/controllers/GameSessions.js (games.packs).
export function gameReadiness({
  status, gameType, gameTypes, tagIds, gameTagId, pictureCount, boardSize, minPictures, practice,
}) {
  const typed = !!gameType;
  const tagged = tagIds.includes(gameTagId);
  const live = status === 'live';
  const isMatchingGrid = gameType === 'matching_grid';
  const enoughPictures = !isMatchingGrid || pictureCount >= minPictures;

  const checks = [
    {
      key: 'type',
      state: typed ? 'ok' : 'missing',
      text: typed ? `Game type: ${gameTypeLabel(gameTypes, gameType)}` : 'Choose a game type',
    },
    {
      key: 'tag',
      state: tagged ? 'ok' : 'missing',
      text: tagged ? 'Tagged “Interactive Game”' : 'Add the “Interactive Game” tag so players see it as a game',
    },
  ];

  if (isMatchingGrid) {
    checks.push(
      {
        key: 'pictures',
        state: pictureCount >= boardSize ? 'ok' : enoughPictures ? 'warn' : 'missing',
        text: pictureCount >= boardSize
          ? `${pictureCount} pictures (the board shows ${boardSize})`
          : enoughPictures
            ? `${pictureCount} pictures. The board shows up to ${boardSize}, so add ${boardSize - pictureCount} more for a full board`
            : `Needs at least ${minPictures} pictures (${boardSize} for a full board)`,
      },
      {
        key: 'practice',
        state: practice ? 'ok' : 'warn',
        text: practice ? 'Practice prompts set' : 'No practice prompts, so the game skips the practice round',
      },
    );
  }

  checks.push({
    key: 'live',
    state: live ? 'ok' : 'missing',
    text: live ? 'Live' : `Set to Live to show it to players (now ${STATUS_META[status].label})`,
  });

  return {
    checks,
    playable: typed && tagged && live && enoughPictures,
    testable: typed && enoughPictures,
  };
}

// Assigned practice pictures win. An empty slot still uses picture 2 or 3
// (indexes 1 and 2), which is what packs were written for before assignment.
function practicePictureForSlot(pictures, assignedId, fallbackIndex, takenId) {
  const belongs = (id) => pictures.some((picture) => picture.id === id);
  if (belongs(assignedId) && assignedId !== takenId) {
    return pictures.find((picture) => picture.id === assignedId) || null;
  }
  if (!assignedId) {
    const fallback = pictures[fallbackIndex];
    if (fallback && fallback.id !== takenId) return fallback;
  }
  return null;
}

export function effectivePracticePictures(pictures, pictureOneId, pictureTwoId) {
  const one = practicePictureForSlot(pictures, pictureOneId, 1, null);
  let two = practicePictureForSlot(pictures, pictureTwoId, 2, one && one.id);
  const fallbackWasTaken = !pictureTwoId && pictures[2] && one && pictures[2].id === one.id;
  if (!two && pictureOneId && fallbackWasTaken) {
    two = pictures.find((picture) => picture.id !== one.id) || null;
  }
  return [one, two];
}
